import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "../../config";
import Footer from "../homepage/footer";

const QuizHistory = ({ userUID }) => {
  const [latestByTopic, setLatestByTopic] = useState({});
  const [allTopics, setAllTopics] = useState([]);
  const [topics, setTopics] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchQuizHistory = async () => {
      try {
        const response = await axios.get(`${API_BASE}/api/quiz-attempts/user/${userUID}`);

        const groupedQuizzes = response.data.reduce((acc, attempt) => {
          const { topic_id, created_at } = attempt;

          if (!acc[topic_id] || new Date(created_at) > new Date(acc[topic_id].created_at)) {
            acc[topic_id] = { ...attempt };
          }

          return acc;
        }, {});

        setLatestByTopic(groupedQuizzes);
      } catch (err) {
        const status = err?.response?.status;
        if (status === 404 || status === 204) {
          setLatestByTopic({});
          return;
        }
        console.error("Error fetching quiz history:", err);
        setError("Failed to load quiz history.");
      }
    };

    const fetchTopics = async () => {
      try {
        const response = await axios.get(`${API_BASE}/api/topics`);

        const topicMap = response.data.reduce((acc, topic) => {
          acc[topic.id] = topic.name;
          return acc;
        }, {});

        setTopics(topicMap);
        setAllTopics(response.data || []);
      } catch (err) {
        console.error("Error fetching topics:", err);
        setError("Failed to load topics.");
      } finally {
        setLoading(false);
      }
    };

    fetchQuizHistory();
    fetchTopics();
  }, [userUID]);

  // Calculate statistics
  const calculateStats = () => {
    const quizzes = Object.values(latestByTopic);
    if (quizzes.length === 0) return { totalQuizzes: 0, avgPercentage: 0, thisWeek: 0, bestScore: 0 };

    const totalScore = quizzes.reduce((sum, quiz) => sum + quiz.score, 0);
    const totalPossible = quizzes.reduce((sum, quiz) => sum + quiz.max_score, 0);
    const avgPercentage = ((totalScore / totalPossible) * 100).toFixed(1);

    const thisWeek = quizzes.filter(quiz => {
      const quizDate = new Date(quiz.created_at);
      const now = new Date();
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return quizDate >= weekAgo;
    }).length;

    const bestPercentage = Math.max(...quizzes.map(q => (q.score / q.max_score) * 100));

    return {
      totalQuizzes: quizzes.length,
      avgPercentage,
      thisWeek,
      bestScore: bestPercentage.toFixed(1)
    };
  };

  const stats = calculateStats();

  // Score color helper
  const getScoreColor = (score, maxScore) => {
    const percentage = (score / maxScore) * 100;
    if (percentage >= 80) return 'bg-green-100 text-green-700 border-green-300';
    if (percentage >= 60) return 'bg-blue-100 text-blue-700 border-blue-300';
    if (percentage >= 40) return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    return 'bg-orange-100 text-orange-700 border-orange-300';
  };

  const getScorePercentage = (score, maxScore) => {
    return ((score / maxScore) * 100).toFixed(1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-teal-600 mx-auto mb-4"></div>
          <p className="text-teal-600 font-semibold">Loading quiz history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md">
          <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-center text-red-600 font-semibold">{error}</p>
        </div>
      </div>
    );
  }

  const topicsWithAttempts = allTopics.filter(t => latestByTopic[t.id]);

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <Helmet>
        <title>Quiz History | MLMaths</title>
        <link rel="canonical" href="https://mlmaths.co.uk/quizhistory" />
        <meta name="description" content="View your quiz history, track your progress across all topics, and see your latest scores on MLMaths." />
      </Helmet>

      <main className="container mx-auto py-10 px-4 sm:px-6 lg:px-12 max-w-7xl flex-grow">
        {/* Header Section */}
        <div className="text-center mb-10">
          <div className="bg-teal-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Quiz History</h1>
          <p className="text-lg text-gray-600">Track your quiz performance and progress</p>
        </div>

        {/* Statistics Cards */}
        {stats.totalQuizzes > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-teal-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium mb-1">Total Quizzes</p>
                  <p className="text-3xl font-bold text-gray-800">{stats.totalQuizzes}</p>
                </div>
                <div className="bg-teal-100 w-12 h-12 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-blue-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium mb-1">Average Score</p>
                  <p className="text-3xl font-bold text-gray-800">{stats.avgPercentage}%</p>
                </div>
                <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-purple-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium mb-1">This Week</p>
                  <p className="text-3xl font-bold text-gray-800">{stats.thisWeek}</p>
                </div>
                <div className="bg-purple-100 w-12 h-12 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-green-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium mb-1">Best Score</p>
                  <p className="text-3xl font-bold text-gray-800">{stats.bestScore}%</p>
                </div>
                <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quiz History List */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border-t-4 border-teal-500">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-800">Recent Quiz Attempts</h2>
            <p className="text-gray-600 mt-1">Your latest score for each topic</p>
          </div>

          {topicsWithAttempts.length === 0 ? (
            <div className="p-12 text-center">
              <div className="bg-gray-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 8h6m-6 4h6" />
                </svg>
              </div>
              <p className="text-gray-600 font-medium text-lg">No quiz attempts yet</p>
              <p className="text-gray-500 text-sm mt-2">Start a quiz to see your history here!</p>
              <button
                onClick={() => navigate('/home')}
                className="mt-6 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold"
              >
                Start Learning
              </button>
            </div>
          ) : (
            <div>
              {/* Desktop: Cards View */}
              <div className="hidden md:block p-6 space-y-4">
                {topicsWithAttempts.map((topic) => {
                  const quiz = latestByTopic[topic.id];
                  const percentage = getScorePercentage(quiz.score, quiz.max_score);
                  return (
                    <div
                      key={topic.id}
                      className="border-2 border-gray-100 rounded-xl p-6 hover:border-teal-200 hover:shadow-md transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-800 mb-2">{topic.name}</h3>
                          <div className="flex items-center gap-6 text-sm">
                            <span className="text-gray-600">
                              <span className="font-semibold text-gray-800">{quiz.score}</span> / {quiz.max_score} marks
                            </span>
                            <span className={`px-3 py-1 rounded-full text-sm font-bold border-2 ${getScoreColor(quiz.score, quiz.max_score)}`}>
                              {percentage}%
                            </span>
                            <span className="text-gray-500 flex items-center gap-1">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              {new Date(quiz.created_at).toLocaleDateString("en-GB", {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                              })}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => navigate(`/quizattempts/${topic.id}`)}
                          className="ml-4 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-semibold"
                        >
                          View All Attempts
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Mobile: Compact Cards */}
              <div className="md:hidden p-4 space-y-3">
                {topicsWithAttempts.map((topic) => {
                  const quiz = latestByTopic[topic.id];
                  const percentage = getScorePercentage(quiz.score, quiz.max_score);
                  return (
                    <div
                      key={topic.id}
                      className="bg-gray-50 rounded-xl p-4 border-l-4 border-teal-500"
                    >
                      <h3 className="font-bold text-gray-800 mb-3">{topic.name}</h3>
                      <div className="space-y-2 mb-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Score:</span>
                          <span className="font-semibold text-gray-800">{quiz.score} / {quiz.max_score}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Percentage:</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-bold ${getScoreColor(quiz.score, quiz.max_score)}`}>
                            {percentage}%
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Date:</span>
                          <span className="text-sm text-gray-500">
                            {new Date(quiz.created_at).toLocaleDateString("en-GB")}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => navigate(`/quizattempts/${topic.id}`)}
                        className="w-full py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-semibold text-sm"
                      >
                        View All Attempts
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Motivational Card */}
        {stats.totalQuizzes > 0 && (
          <div className="mt-8 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl shadow-xl p-8 text-white">
            <div className="flex items-center gap-4 mb-4">
              <div className="bg-white bg-opacity-20 w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <h3 className="text-2xl font-bold">
                  {stats.avgPercentage >= 80 ? "Outstanding Performance!" :
                   stats.avgPercentage >= 60 ? "Great Job!" :
                   "Keep Practicing!"}
                </h3>
                <p className="text-teal-100">
                  {stats.avgPercentage >= 80 ? "You're excelling at these topics" :
                   stats.avgPercentage >= 60 ? "You're making solid progress" :
                   "Every attempt helps you improve"}
                </p>
              </div>
            </div>
            <p className="text-teal-50 text-lg">
              You've completed <span className="font-bold">{stats.totalQuizzes}</span> quiz{stats.totalQuizzes !== 1 ? 'zes' : ''} with an average score of <span className="font-bold">{stats.avgPercentage}%</span>.
              {stats.thisWeek > 0 && ` You've taken ${stats.thisWeek} quiz${stats.thisWeek !== 1 ? 'zes' : ''} this week!`} Keep up the excellent work!
            </p>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default QuizHistory;